// PRD 14 §3.5 — slide panel for Term Type CRUD. Root-only.
// Lists every type with required/optional + version-count badges, lock icon
// on default seeds, and Edit / Delete actions. Add and Edit reuse the same
// inline form (TypeEditForm). Delete is gated by `deletable` (lock) and
// `versionCount === 0` (legal preservation).

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';

import { useDeleteTermType } from '../hooks/use-term-mutations';
import { useTermTypesQuery } from '../hooks/use-terms';
import type { TermType } from '../types/terms.types';
import { getTermsErrorMessage } from '../utils/error-message';
import { TypeEditForm } from './TypeEditForm';

interface TermsTypeManagerPanelProps {
  open: boolean;
  onClose: () => void;
}

// Discriminated editing target so 'new' and 'edit' can both inhabit the same
// slot (only one inline form open at a time).
type EditingTarget = { kind: 'edit'; typeId: number } | { kind: 'new' } | null;

function TypeRow({
  type,
  onEdit,
  onDelete,
}: {
  type: TermType;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('terms');
  const isLocked = !type.deletable;
  const hasVersions = type.versionCount > 0;
  // Delete is hidden entirely on locked (default) seeds and disabled on
  // user-added types that already have version history (PRD 14 §3.5 step 5).
  const showDelete = !isLocked;
  const deleteDisabled = hasVersions;
  const deleteTooltip = deleteDisabled ? t('typeManager.tooltips.hasVersions') : undefined;

  return (
    <div
      style={{
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--card)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 4,
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--foreground)' }}>{type.name}</span>
          <span
            className={`badge ${
              type.required ? 'badge-destructive' : 'badge-secondary'
            }`}
          >
            {type.required
              ? t('typeManager.badges.required')
              : t('typeManager.badges.optional')}
          </span>
          {/* SHCT-683 — the lock sits AFTER the 필수/선택 badge, not before the
              name: PRD 14 §2 mock row `서비스 이용약관 [필수] 🔒 [버전 4건]`. */}
          {isLocked ? (
            <span
              title={t('typeManager.tooltips.locked')}
              style={{ display: 'inline-flex', color: 'var(--muted-foreground)' }}
            >
              <Lock size={12} />
            </span>
          ) : null}
          <span className="badge badge-outline">
            {t('typeManager.versionCount', { count: type.versionCount })}
          </span>
        </div>
        {type.description ? (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--muted-foreground)',
              lineHeight: 1.4,
            }}
          >
            {type.description}
          </div>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={onEdit}
        >
          <Pencil size={12} />
          {t('typeManager.actions.edit')}
        </button>
        {showDelete ? (
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={onDelete}
            disabled={deleteDisabled}
            title={deleteTooltip}
          >
            <Trash2 size={12} />
            {t('typeManager.actions.delete')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function TermsTypeManagerPanel({ open, onClose }: TermsTypeManagerPanelProps) {
  const { t } = useTranslation('terms');
  const { data: types, isLoading } = useTermTypesQuery();
  const deleteMutation = useDeleteTermType();

  const [editing, setEditing] = useState<EditingTarget>(null);
  const [deleteTarget, setDeleteTarget] = useState<TermType | null>(null);

  const handleClose = () => {
    // Discard any in-progress add/edit when the panel closes — matches the
    // prototype, which has no dirty-check on the slide panel.
    setEditing(null);
    setDeleteTarget(null);
    onClose();
  };

  const closeForm = () => setEditing(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteMutation.isPending) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('toasts.typeDeleted'));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getTermsErrorMessage(err, t, 'errors.deleteTypeFailed'));
    }
  };

  return (
    <>
      <SlidePanel
        open={open}
        onClose={handleClose}
        title={t('typeManager.title')}
        width={520}
      >
        {isLoading ? (
          <div style={{ color: 'var(--muted-foreground)' }}>{t('list.loading')}</div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              minHeight: '100%',
            }}
          >
            {/* SHCT-683 — PRD 14 §2 mock: `총 N건` on the left, `[+ 약관 종류 추가]`
                on the right, both ABOVE the list (the add button used to sit at
                the bottom and the total was missing entirely). */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-1)',
              }}
            >
              {/* SHCT-683 — the prototype emphasises only the NUMBER, not the whole
                  phrase: `총` and `건` stay muted, the count is --foreground/600
                  (terms-management.html, 약관 종류 관리 drawer). Rendering the string
                  in one muted span lost that, which is what QC flagged after the
                  first pass. The count is wrapped in the i18n string itself so the
                  emphasis follows the number wherever a locale puts it — English
                  reads `N total`, so a hardcoded prefix/suffix split would break. */}
              <span style={{ fontSize: 14, color: 'var(--muted-foreground)' }}>
                <Trans
                  i18nKey="list.totalCount"
                  t={t}
                  values={{ count: types?.length ?? 0 }}
                  components={{
                    count: <span style={{ color: 'var(--foreground)', fontWeight: 600 }} />,
                  }}
                />
              </span>
              <button
                type="button"
                className="btn btn-default btn-primary"
                onClick={() => setEditing({ kind: 'new' })}
                disabled={editing !== null}
              >
                <Plus size={14} />
                {t('typeManager.addType')}
              </button>
            </div>

            {!types || types.length === 0 ? (
              <div style={{ color: 'var(--muted-foreground)' }}>{t('typeManager.empty')}</div>
            ) : (
              types.map((type) =>
                editing?.kind === 'edit' && editing.typeId === type.id ? (
                  <TypeEditForm key={type.id} type={type} onClose={closeForm} />
                ) : (
                  <TypeRow
                    key={type.id}
                    type={type}
                    onEdit={() => setEditing({ kind: 'edit', typeId: type.id })}
                    onDelete={() => setDeleteTarget(type)}
                  />
                ),
              )
            )}

            {editing?.kind === 'new' ? <TypeEditForm type={null} onClose={closeForm} /> : null}

            {/* SHCT-683 — PRD 14 §2 mock closes the panel with a `[닫기]` button,
                which was missing (only the ✕ in the header existed). */}
            <div
              style={{
                marginTop: 'auto',
                paddingTop: 'var(--space-4)',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <button
                type="button"
                className="btn btn-default btn-secondary"
                onClick={handleClose}
              >
                {t('typeManager.close')}
              </button>
            </div>
          </div>
        )}
      </SlidePanel>

      <ConfirmModal
        open={deleteTarget !== null}
        title={t('typeManager.confirmDelete.title')}
        message={
          deleteTarget
            ? t('typeManager.confirmDelete.message', { name: deleteTarget.name })
            : ''
        }
        confirmLabel={t('typeManager.confirmDelete.confirm')}
        cancelLabel={t('typeManager.confirmDelete.cancel')}
        confirmVariant="destructive"
        loading={deleteMutation.isPending}
        loadingLabel={t('typeManager.confirmDelete.deleting')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (deleteMutation.isPending) return;
          setDeleteTarget(null);
        }}
      />
    </>
  );
}
